
export function apiError(e: any): string {
  if (typeof e === "string") return e;
  return e?.error || e?.message || e?.response?.data?.error || e?.response?.data?.message || "Unknown error";
}

export function formatTimestamp(ts: string | null | undefined): string {
  if (!ts) return "-";
  const d = new Date(ts);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "-";
  if (ms < 1) return "<1ms";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function durationClass(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "";
  if (ms < 100) return "text-emerald-600 dark:text-emerald-400";
  if (ms < 500) return "text-sky-600 dark:text-sky-400";
  if (ms < 2000) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

export function elapsedClass(ms: number): string {
  if (ms < 2000) return "text-emerald-600 dark:text-emerald-400";
  if (ms < 10000) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

export function timeAgo(ts: string | null | undefined): string {
  if (!ts) return "-";
  const now = Date.now();
  const then = new Date(ts).getTime();
  const diff = now - then;
  if (diff < 0) return "just now";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function formatUptime(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return "-";
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

export type StatusType =
  | "SUCCESS" | "COMPLETED"
  | "ERROR" | "FAILURE"
  | "DENIED"
  | "WARNING"
  | "TIMEOUT"
  | "PARTIAL"
  | "IN_PROGRESS" | "STARTED"
  | string;

export function statusVariant(
  status: StatusType
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "SUCCESS":
    case "COMPLETED":
      return "default";
    case "ERROR":
    case "FAILURE":
    case "FAILED":
    case "DENIED":
    case "TIMEOUT":
      return "destructive";
    default:
      return "secondary";
  }
}

export function statusColor(status: StatusType): string {
  switch (status) {
    case "SUCCESS":
    case "COMPLETED":
      return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30";
    case "ERROR":
    case "FAILURE":
    case "FAILED":
      return "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30";
    case "DENIED":
      return "bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30";
    case "WARNING":
      return "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30";
    case "TIMEOUT":
      return "bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30";
    case "PARTIAL":
      return "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30";
    case "IN_PROGRESS":
    case "STARTED":
      return "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30";
    default:
      return "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]";
  }
}

export function moduleColor(module: string): string {
  const map: Record<string, string> = {
    WS_SERVER: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
    WS_CLIENT: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30",
    // MCP transport legs get their own distinct hues (agent-facing vs server-facing)
    MCP_INBOUND: "bg-lime-500/15 text-lime-600 dark:text-lime-400 border-lime-500/30",
    MCP_OUTBOUND: "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30",
    ORCHESTRATION_LAYER: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30",
    CAPABILITY_REGISTRY: "bg-teal-500/15 text-teal-600 dark:text-teal-400 border-teal-500/30",
    AGENT_REGISTRY: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-500/30",
    CAPABILITY_PROFILES: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400 border-fuchsia-500/30",
    PDP: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
    // STS (token-mint + key rotate/retire) — its own distinct hue
    STS: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30",
    SYSTEM: "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]",
    SERVER_CONFIG: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  };
  return map[module] || "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]";
}


const EVENT_TYPE_LABELS: Record<string, string> = {
  ORCHESTRATION_TOOL_EXTRACTED: "Tool call parsed",
  CAPABILITY_ACCESS_GRANTED: "Capability allowed",
  CAPABILITY_ACCESS_DENIED: "Capability denied",
  PDP_EVALUATION_REQUESTED: "Policy evaluation requested",
  PDP_DECISION_RENDERED: "Policy decision rendered",
  STS_TOKEN_MINTED: "Delegation token minted (OBO)",
  STS_KEY_ROTATED: "STS signing key rotated",
  STS_KEY_RETIRED: "STS signing key retired",
  ORCHESTRATION_ERROR: "Orchestration error",
  AGENT_CONNECTION_REJECTED: "Agent request rejected",
  AGENT_DEPROVISIONED: "Agent deprovisioned",
};

export function eventTypeLabel(type: string | null | undefined): string {
  if (!type) return "Event";
  if (EVENT_TYPE_LABELS[type]) return EVENT_TYPE_LABELS[type];
  return type
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function protocolColor(protocol: string): string {
  if (protocol === "A2A") return "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400 border-fuchsia-500/30";
  if (protocol === "MCP") return "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]";
  return "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]";
}

export function capabilityTypeColor(type: string): string {
  const map: Record<string, string> = {
    TOOL: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30",
    PROMPT: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30",
    RESOURCE: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30",
    SKILL: "bg-teal-500/15 text-teal-600 dark:text-teal-400 border-teal-500/30",
  };
  return map[type] || "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]";
}

export function severityColor(severity: string): string {
  switch ((severity || "").toLowerCase()) {
    case "critical":
      return "bg-red-500";
    case "high":
      return "bg-orange-500";
    case "medium":
      return "bg-amber-500";
    case "low":
      return "bg-sky-500";
    default:
      return "bg-slate-500";
  }
}

export function callerTypeLabel(tokenType: string | null | undefined): {
  label: string;
  color: string;
} {
  if (!tokenType) return { label: "Unknown", color: "bg-card text-muted-foreground border border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]" };
  if (tokenType === "HUMAN_DELEGATED")
    return { label: "Human", color: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" };
  if (tokenType === "AUTOMATED_AGENT")
    return { label: "Automated", color: "bg-orange-500/15 text-orange-600 dark:text-orange-400" };
  return { label: tokenType, color: "bg-card text-muted-foreground border border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]" };
}

export function connectionStatusColor(status: string): string {
  switch (status) {
    case "CONNECTED":
      return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30";
    case "DISCONNECTED":
      return "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30";
    default:
      return "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]";
  }
}

export function approvalStatusColor(status: string): string {
  switch (status) {
    case "APPROVED":
      return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30";
    case "PENDING":
      return "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30";
    case "BLOCKED":
      return "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30";
    default:
      return "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]";
  }
}

export function identityStatusColor(status: string): string {
  switch (status) {
    case "ACTIVE":
      return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30";
    case "PENDING":
      return "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30";
    case "BLOCKED":
      return "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30";
    default:
      return "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]";
  }
}

export function detectIdpType(issuerUri: string | null | undefined): string {
  if (!issuerUri) return "Unknown";
  const uri = issuerUri.toLowerCase();
  if (uri.includes("keycloak") || uri.includes("/realms/")) return "Keycloak";
  if (uri.includes("login.microsoftonline") || uri.includes("sts.windows.net")) return "Azure AD";
  if (uri.includes("okta.com") || uri.includes("oktapreview.com")) return "Okta";
  if (uri.includes("auth0.com")) return "Auth0";
  if (uri.includes("accounts.google.com")) return "Google";
  if (uri.includes("cognito-idp") || uri.includes("amazonaws.com")) return "AWS Cognito";
  return "Custom OIDC";
}

export function syntaxHighlightJson(obj: any): string {
  if (obj === null || obj === undefined) return "null";
  const json = typeof obj === "string" ? obj : JSON.stringify(obj, null, 2);
  return json;
}

export function prettyJson(raw: any): string {
  if (raw === null || raw === undefined) return "null";
  if (typeof raw === "string") {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }
  return JSON.stringify(raw, null, 2);
}

export function normalizeResponsePayload(payload: any): any {
  if (!payload) return null;
  if (payload.content) {
    if (Array.isArray(payload.content)) {
      const textItem = payload.content.find((c: any) => c.type === "text" && c.text);
      if (textItem) {
        try {
          return JSON.parse(textItem.text);
        } catch {
          return textItem.text;
        }
      }
    }
    return payload.content;
  }
  return payload;
}

export function highlightCedar(code: string): string {
  if (!code) return "";

  const KEYWORDS = new Set(["permit", "forbid", "when", "unless", "if", "then", "else", "in", "has", "like", "is"]);
  const ENTITIES = new Set(["principal", "action", "resource", "context"]);

  const TOKEN_RE =
    /(\/\/.*$)|("(?:[^"\\]|\\.)*")|(\b[A-Z]\w*::\w*(?:::\w*)*\b)|(\|\||&&|==|!=|>=|<=)|(\b\w+\b)/gm;

  function esc(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  // Process line by line so ^ anchors in comments work
  return code.split("\n").map((line) => {
    let result = "";
    let lastIndex = 0;

    TOKEN_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = TOKEN_RE.exec(line)) !== null) {
      // Emit any gap text (punctuation, whitespace, braces, etc.) escaped
      if (m.index > lastIndex) {
        result += esc(line.slice(lastIndex, m.index));
      }
      lastIndex = m.index + m[0].length;

      const [full, comment, str, entityRef, op, word] = m;

      if (comment) {
        result += `<span class="text-slate-500 italic">${esc(comment)}</span>`;
      } else if (str) {
        result += `<span class="text-emerald-600 dark:text-emerald-400">${esc(str)}</span>`;
      } else if (entityRef) {
        result += `<span class="text-sky-600 dark:text-sky-400">${esc(entityRef)}</span>`;
      } else if (op) {
        result += `<span class="text-amber-600 dark:text-amber-400">${esc(op)}</span>`;
      } else if (word && KEYWORDS.has(word)) {
        result += `<span class="text-purple-600 dark:text-purple-400 font-semibold">${esc(word)}</span>`;
      } else if (word && ENTITIES.has(word)) {
        result += `<span class="text-cyan-600 dark:text-cyan-400">${esc(word)}</span>`;
      } else {
        result += esc(full);
      }
    }
    // Remaining tail
    if (lastIndex < line.length) {
      result += esc(line.slice(lastIndex));
    }
    return result;
  }).join("\n");
}

// ── Normalize Hourly Data (fill 24h gaps) ───────────────────
export function normalizeHourly(
  raw: Array<{ hour: string; count: number; errors?: number; avgLatency?: number }>,
  hours: number = 24
): Array<{ hour: string; count: number; errors: number; avgLatency: number }> {
  const map = new Map<string, any>();
  raw.forEach((entry) => map.set(entry.hour, entry));

  const result: Array<{ hour: string; count: number; errors: number; avgLatency: number }> = [];
  const now = new Date();
  for (let i = hours - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 3600000);
    const key = d.toISOString().slice(0, 13) + ":00";
    const entry = map.get(key);
    result.push({
      hour: key,
      count: entry?.count || 0,
      errors: entry?.errors || 0,
      avgLatency: entry?.avgLatency || 0,
    });
  }
  return result;
}

// ── Copy to Clipboard Helper ────────────────────────────────
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text ?? "");
    return true;
  } catch {
    return false;
  }
}

// ── Success Rate Color ──────────────────────────────────────
export function successRateColor(rate: number): string {
  if (rate >= 99) return "text-emerald-600 dark:text-emerald-400";
  if (rate >= 95) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

// ── Latency Percentile Color ────────────────────────────────
export function latencyColor(label: string): string {
  switch (label) {
    case "p50":
      return "text-emerald-600 dark:text-emerald-400";
    case "p95":
      return "text-amber-600 dark:text-amber-400";
    case "p99":
      return "text-red-600 dark:text-red-400";
    default:
      return "text-slate-400";
  }
}

// ── Effect Badge Color (policies) ───────────────────────────
export function effectColor(effect: string): string {
  switch (effect) {
    case "PERMIT":
      return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30";
    case "FORBID":
      return "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30";
    default:
      return "bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]";
  }
}

// ── Grace Period Options ────────────────────────────────────
// backend gracePeriodMinutes is an Integer (minutes, 0-1440)
export const GRACE_PERIOD_OPTIONS = [
  { value: "5", label: "5 minutes" },
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "120", label: "2 hours" },
  { value: "480", label: "8 hours" },
  { value: "1440", label: "24 hours" },
];

// ── Token Classification Options ────────────────────────────
// backend AuthConfigRequest @Pattern ^(introspect|jwt-signals|conservative)$
export const TOKEN_CLASSIFICATION_OPTIONS = [
  { value: "jwt-signals", label: "Strict (Recommended)", description: "JWT-signal based classification" },
  { value: "introspect", label: "Introspect (Full)", description: "Introspect tokens at the IdP" },
  { value: "conservative", label: "Conservative (All Human)", description: "Treat all tokens conservatively" },
];
